import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Zap, AlertTriangle, ListChecks, Clock, CreditCard, X } from 'lucide-react';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContainer = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: ${slideIn} 0.3s ease-out;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94A3B8;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Very Sans Medium', sans-serif;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0;
  padding-right: 3rem;
`;

const Subtitle = styled.p`
  color: #94A3B8;
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 0.95rem;
  margin-top: 0.5rem;
`;

const ModalContent = styled.div`
  padding: 1.5rem 2rem 2rem 2rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const SectionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  flex-shrink: 0;
`;

const RequirementsIcon = styled(SectionIcon)`
  background: rgba(239, 68, 68, 0.2);
  color: #ef4444;
`;

const DisclaimerIcon = styled(SectionIcon)`
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
`;

const StepsIcon = styled(SectionIcon)`
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
`;

const InfoIcon = styled(SectionIcon)`
  background: rgba(59, 130, 246, 0.2);
  color: #3B82F6;
`;

const CardIcon = styled(SectionIcon)`
  background: rgba(168, 85, 247, 0.2);
  color: #a855f7;
`;

const SectionTitle = styled.h3`
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
`;

const SectionText = styled.p`
  color: #94A3B8;
  font-family: 'Very Sans Light', sans-serif;
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0;
  
  strong {
    color: #ffffff;
    font-family: 'Very Sans Medium', sans-serif;
  }
`;

const StepsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const StepItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &::before {
    content: '${props => props.number}';
    background: #3B82F6;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 600;
    margin-right: 0.75rem;
    margin-top: 0.1rem;
    flex-shrink: 0;
    font-family: 'Very Sans Bold', sans-serif;
  }
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.strong`
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-weight: 600;
  display: block;
  margin-bottom: 0.25rem;
`;

const StepDescription = styled.span`
  color: #94A3B8;
  font-family: 'Very Sans Light', sans-serif;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const HighlightBox = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin-top: 0.75rem;
`;

const HighlightText = styled.p`
  color: #94A3B8;
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 0.85rem;
  margin: 0;
  
  strong {
    color: #3B82F6;
    font-family: 'Very Sans Bold', sans-serif;
  }
`;

const ActionButton = styled.button`
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-family: 'Very Sans Bold', sans-serif;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1.5rem;
  width: 100%;
  
  &:hover {
    background: #2563EB;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const InfoModal = ({ onClose, onProceed }) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  const handleProceed = () => {
    if (onProceed) {
      onProceed();
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <CloseButton onClick={onClose}>
            <X size={20} strokeWidth={2.5} />
          </CloseButton>
          <Title>Demo Setup Guide</Title>
          <Subtitle>Please read the following information before proceeding</Subtitle>
        </ModalHeader>

        <ModalContent>
          <Section>
            <SectionHeader>
              <RequirementsIcon>
                <Zap size={18} strokeWidth={2.5} />
              </RequirementsIcon>
              <SectionTitle>Requirements</SectionTitle>
            </SectionHeader>
            <SectionText>
              You must have MetaMask installed and be connected to <strong>Hydrix testnet</strong>. 
              You must also have some <strong>HYX (testnet)</strong> and some <strong>USDH (testnet)</strong> available.
            </SectionText>
          </Section>

          <Section>
            <SectionHeader>
              <DisclaimerIcon>
                <AlertTriangle size={18} strokeWidth={2.5} />
              </DisclaimerIcon>
              <SectionTitle>Sandbox Environment Notice</SectionTitle>
            </SectionHeader>
            <SectionText>
              This is a demonstration environment using test data and sandbox APIs. 
              No real money will be processed. Any data loss or issues are at your own risk. 
              This demo is for educational and testing purposes only.
            </SectionText>
          </Section>

          <Section>
            <SectionHeader>
              <StepsIcon>
                <ListChecks size={18} strokeWidth={2.5} />
              </StepsIcon>
              <SectionTitle>How to Navigate Through the Demo</SectionTitle>
            </SectionHeader>
            <StepsList>
              <StepItem number="1">
                <StepContent>
                  <StepTitle>Create Virtual Card</StepTitle>
                  <StepDescription>
                    Start by generating a new virtual card through the Stripe API integration.
                  </StepDescription>
                </StepContent>
              </StepItem>
              <StepItem number="2">
                <StepContent>
                  <StepTitle>Set Up Subscription</StepTitle>
                  <StepDescription>
                    Configure a recurring payment subscription using the created virtual card.
                  </StepDescription>
                </StepContent>
              </StepItem>
            </StepsList>
          </Section>

          <Section>
            <SectionHeader>
              <InfoIcon>
                <Clock size={18} strokeWidth={2.5} />
              </InfoIcon>
              <SectionTitle>Demo Payment Schedule</SectionTitle>
            </SectionHeader>
            <SectionText>
              Recurring payments are hardcoded to process every <strong>5 minutes</strong> for demonstration purposes. 
              In a production environment, these would typically run on your chosen frequency.
            </SectionText>
            <HighlightBox>
              <HighlightText>
                <strong>Quick Demo:</strong> You'll see results faster with our accelerated timeline!
              </HighlightText>
            </HighlightBox>
          </Section>

          <Section>
            <SectionHeader>
              <CardIcon>
                <CreditCard size={18} strokeWidth={2.5} />
              </CardIcon>
              <SectionTitle>View Card Details</SectionTitle>
            </SectionHeader>
            <SectionText>
              You can view your Stripe virtual card details at any time by clicking the 
              <strong> "My Card"</strong> button in the interface.
            </SectionText>
          </Section>

          <ActionButton onClick={handleProceed}>
            Got it, let's proceed! →
          </ActionButton>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default InfoModal;